import { useEffect, useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@frontdoor/ui/components/alert';
import { Button } from '@frontdoor/ui/components/button';
import { Field, FieldError, FieldLabel } from '@frontdoor/ui/components/field';
import { Input } from '@frontdoor/ui/components/input';
import { zodResolver } from '@hookform/resolvers/zod';
import { format, parse } from 'date-fns';
import { CircleAlert, Loader2 } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useOutletContext, useParams, useSearchParams } from 'react-router';

import type { TrackingVerifyPayload, TrackingVerifyResponse } from '@/api/generated';
import { useVerifyTracking } from '@/api/use-verify-tracking.ts';
import { DateInput } from '@/components/DateInput.tsx';
import type { TrackingOutletContext } from '@/components/layout/TrackingLayout.tsx';
import { isPostHogEnabled, posthog } from '@/lib/posthog.tsx';
import { type VerificationFormData, verificationSchema } from '@/lib/schemas.ts';

import { StatusPage } from './StatusPage.tsx';

function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (isPostHogEnabled) {
    posthog.capture(event, properties);
  }
}

export function VerificationPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const [searchParams] = useSearchParams();
  const [orderData, setOrderData] = useState<TrackingVerifyPayload | null>(null);
  const outletContext = useOutletContext<TrackingOutletContext | null>();
  const bypassAttempted = useRef(false);

  const verifyMutation = useVerifyTracking();

  const isMockBypass = import.meta.env.VITE_ENABLE_MOCKS === 'true' && searchParams.has('bypass');

  useEffect(() => {
    if (!isMockBypass || !publicId || bypassAttempted.current) return;
    bypassAttempted.current = true;

    fetch(`/api/v4/tracking/${publicId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ last_name: 'Mock', date_of_birth: '2000-01-01' }),
    }).then(async (res) => {
      if (!res.ok) return;
      const envelope = (await res.json()) as TrackingVerifyResponse;
      const response = envelope.payload;
      setOrderData(response);
      outletContext?.setHeaderPatientInfo({
        patientName: `${response.order.patient.first_name} ${response.order.patient.last_name}`,
        orderId: String(response.order.order_id),
      });
    });
  }, [isMockBypass, publicId]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<VerificationFormData>({
    resolver: zodResolver(verificationSchema),
    defaultValues: {
      last_name: '',
      date_of_birth: '',
    },
  });

  const onSubmit = (data: VerificationFormData) => {
    if (!publicId) return;

    trackEvent('tracking_verification_submitted', { public_id: publicId });

    const apiDob = format(parse(data.date_of_birth, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd');

    verifyMutation.mutate(
      {
        publicId,
        last_name: data.last_name,
        date_of_birth: apiDob,
      },
      {
        onSuccess: (response) => {
          trackEvent('tracking_verification_success', { public_id: publicId });
          setOrderData(response);
          outletContext?.setHeaderPatientInfo({
            patientName: `${response.order.patient.first_name} ${response.order.patient.last_name}`,
            orderId: String(response.order.order_id),
          });
        },
        onError: () => {
          trackEvent('tracking_verification_failed', { public_id: publicId });
        },
      }
    );
  };

  if (orderData) {
    return <StatusPage data={orderData} publicId={publicId!} />;
  }

  return (
    <div className="mx-auto flex w-full max-w-[calc(860px+32px)] flex-1 flex-col px-4 pt-12 md:px-8 md:pt-16">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-8 flex flex-col gap-2">
          <p className="text-base">Track an order</p>
          <h1 className="font-serif text-[32px] leading-tight">
            Verify patient info to see order details
          </h1>
        </div>

        {verifyMutation.isError && (
          <Alert className="mb-6 border-none bg-rose-200 text-rose-700">
            <CircleAlert className="size-4" />
            <AlertTitle>Verification failed</AlertTitle>
            <AlertDescription className="text-rose-700/90">
              We couldn't verify your identity. Please check your information and try again.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
          <Field data-invalid={!!errors.last_name}>
            <FieldLabel htmlFor="last_name">Patient last name</FieldLabel>
            <Controller
              name="last_name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="last_name"
                  placeholder="Last name"
                  autoComplete="family-name"
                  aria-invalid={!!errors.last_name}
                  aria-describedby={errors.last_name ? 'last_name-error' : undefined}
                />
              )}
            />
            <FieldError id="last_name-error" errors={errors.last_name ? [errors.last_name] : []} />
          </Field>

          <Field data-invalid={!!errors.date_of_birth}>
            <FieldLabel htmlFor="date_of_birth">Patient date of birth</FieldLabel>
            <Controller
              name="date_of_birth"
              control={control}
              render={({ field }) => (
                <DateInput
                  {...field}
                  id="date_of_birth"
                  aria-invalid={!!errors.date_of_birth}
                  aria-describedby={errors.date_of_birth ? 'date_of_birth-error' : undefined}
                />
              )}
            />
            <FieldError
              id="date_of_birth-error"
              errors={errors.date_of_birth ? [errors.date_of_birth] : []}
            />
          </Field>

          <Button type="submit" className="mt-2 w-full" disabled={verifyMutation.isPending}>
            {verifyMutation.isPending ? (
              <>
                <Loader2 className="animate-spin" />
                Verifying...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
